class StickyProductBarController {
  constructor(productSection, addToCartRoot, productForm) {
    this.productSection = productSection;
    this.addToCartRoot = addToCartRoot;
    this.productForm = productForm;
    this.isAddToCartVisible = true;
    this.isFooterVisible = false;
    this.isVisible = false;
    this.observedAddToCartRoot = null;
    this.observedFooter = null;
    this.footer = document.querySelector('.footer');
    this.stickyRoot = null;
    this.stickyButton = null;
    this.variantTarget = null;

    this.onAddToCartIntersection = this.onAddToCartIntersection.bind(this);
    this.onFooterIntersection = this.onFooterIntersection.bind(this);
    this.onStickyRootClick = this.onStickyRootClick.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onVariantChanged = this.onVariantChanged.bind(this);

    this.addToCartObserver = new IntersectionObserver(
      this.onAddToCartIntersection,
      {
        threshold: 0.01,
      },
    );

    if (this.footer) {
      this.footerObserver = new IntersectionObserver(
        this.onFooterIntersection,
        {
          threshold: 0.01,
        },
      );
    }

    window.addEventListener('resize', this.onResize);
    document.addEventListener('variant:changed', this.onVariantChanged);

    this.refreshElements();

    if (!this.stickyRoot || !this.addToCartRoot) {
      return;
    }

    this.syncObservers();
    this.updateVisibility();
    this.updateHeight();
  }

  setProductForm(productForm) {
    this.productForm = productForm;
    this.addToCartRoot = this.resolveAddToCartRoot();
    this.refreshElements();
    this.syncObservers();
    this.updateVisibility();
    this.updateHeight();
  }

  resolveAddToCartRoot() {
    const currentRoot = this.productForm?.closest(
      '[data-product-add-to-cart-block]',
    );

    if (currentRoot) {
      return currentRoot;
    }

    if (this.addToCartRoot?.isConnected) {
      return this.addToCartRoot;
    }

    return this.productSection?.querySelector(
      '[data-product-add-to-cart-block][data-product-sticky-enabled="true"]',
    );
  }

  getStickyOwnerId() {
    return this.addToCartRoot?.dataset.productAddToCartBlock || '';
  }

  getTeleportedStickyRoot() {
    const stickyOwnerId = this.getStickyOwnerId();

    if (!stickyOwnerId) {
      return null;
    }

    return document.body.querySelector(
      `[data-product-sticky-cart][data-product-sticky-owner="${stickyOwnerId}"]`,
    );
  }

  refreshElements() {
    const nextAddToCartRoot = this.resolveAddToCartRoot();

    if (nextAddToCartRoot !== this.addToCartRoot) {
      this.addToCartRoot = nextAddToCartRoot;
    }

    const inlineStickyRoot = this.addToCartRoot?.querySelector(
      '[data-product-sticky-cart]',
    );
    const teleportedStickyRoot = this.getTeleportedStickyRoot();

    if (
      inlineStickyRoot &&
      teleportedStickyRoot &&
      inlineStickyRoot !== teleportedStickyRoot
    ) {
      if (this.stickyRoot === teleportedStickyRoot) {
        this.teardownStickyRoot(true);
      } else {
        teleportedStickyRoot.remove();
      }
    }

    const nextStickyRoot = inlineStickyRoot || this.getTeleportedStickyRoot();

    if (nextStickyRoot !== this.stickyRoot) {
      this.teardownStickyRoot();
      this.stickyRoot = nextStickyRoot || null;
      this.setupStickyRoot();
    } else if (this.stickyRoot) {
      this.stickyButton = this.stickyRoot.querySelector(
        '.product-sticky-cart__button',
      );
    }

    this.variantTarget = this.productSection?.querySelector(
      '[data-product-variants-target]',
    );
  }

  setupStickyRoot() {
    if (!this.stickyRoot) {
      this.stickyButton = null;
      return;
    }

    this.stickyRoot.addEventListener('click', this.onStickyRootClick);

    if (this.stickyRoot.parentElement !== document.body) {
      document.body.appendChild(this.stickyRoot);
    }

    this.stickyButton = this.stickyRoot.querySelector(
      '.product-sticky-cart__button',
    );
  }

  teardownStickyRoot(remove = false) {
    if (!this.stickyRoot) {
      this.stickyButton = null;
      return;
    }

    this.stickyRoot.removeEventListener('click', this.onStickyRootClick);

    if (remove && this.stickyRoot.isConnected) {
      this.stickyRoot.remove();
    }

    this.stickyRoot = null;
    this.stickyButton = null;
  }

  isElementVisible(element) {
    if (!(element instanceof HTMLElement)) {
      return false;
    }

    const rect = element.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
  }

  syncObservers() {
    if (this.observedAddToCartRoot !== this.addToCartRoot) {
      if (this.observedAddToCartRoot) {
        this.addToCartObserver.unobserve(this.observedAddToCartRoot);
      }

      if (this.addToCartRoot) {
        this.addToCartObserver.observe(this.addToCartRoot);
        this.isAddToCartVisible = this.isElementVisible(this.addToCartRoot);
      } else {
        this.isAddToCartVisible = false;
      }

      this.observedAddToCartRoot = this.addToCartRoot;
    }

    if (this.footerObserver && this.observedFooter !== this.footer) {
      if (this.observedFooter) {
        this.footerObserver.unobserve(this.observedFooter);
      }

      if (this.footer) {
        this.footerObserver.observe(this.footer);
        this.isFooterVisible = this.isElementVisible(this.footer);
      } else {
        this.isFooterVisible = false;
      }

      this.observedFooter = this.footer;
    }
  }

  onAddToCartIntersection(entries) {
    const [entry] = entries;
    this.isAddToCartVisible = Boolean(entry && entry.isIntersecting);
    this.updateVisibility();
  }

  onFooterIntersection(entries) {
    const [entry] = entries;
    this.isFooterVisible = Boolean(entry && entry.isIntersecting);
    this.updateVisibility();
  }

  onStickyRootClick(event) {
    const stickyButton = event.target.closest('.product-sticky-cart__button');

    if (
      !stickyButton ||
      !this.stickyRoot ||
      !this.stickyRoot.contains(stickyButton)
    ) {
      return;
    }

    if (
      stickyButton.hasAttribute('disabled') ||
      stickyButton.getAttribute('aria-disabled') === 'true'
    ) {
      return;
    }

    if (this.addToCartRoot?.dataset.productStickyMode === 'add-to-cart') {
      this.productForm?.submitProductForm(stickyButton);
      return;
    }

    this.scrollToVariants();
  }

  onResize() {
    this.updateHeight();
  }

  onVariantChanged() {
    window.requestAnimationFrame(() => {
      this.refreshElements();
      this.syncObservers();
      this.updateHeight();
      this.updateVisibility();
    });
  }

  scrollToVariants() {
    if (!this.variantTarget) {
      return;
    }

    const headerGroup = document.querySelector('.header-group');
    const headerOffset =
      headerGroup instanceof HTMLElement
        ? headerGroup.getBoundingClientRect().height
        : 0;
    const targetTop =
      this.variantTarget.getBoundingClientRect().top +
      window.scrollY -
      headerOffset -
      24;

    window.scrollTo({
      top: Math.max(targetTop, 0) - 300,
      behavior: 'smooth',
    });
  }

  updateVisibility() {
    this.refreshElements();

    if (!this.stickyRoot) {
      this.resetStickyLayout();
      return;
    }

    const shouldShow = !this.isAddToCartVisible && !this.isFooterVisible;

    this.isVisible = shouldShow;
    this.stickyRoot.classList.toggle('translate-y-0', shouldShow);
    this.stickyRoot.classList.toggle('translate-y-full', !shouldShow);
    this.stickyRoot.classList.toggle('pointer-events-auto', shouldShow);
    this.stickyRoot.classList.toggle('pointer-events-none', !shouldShow);
    // this.stickyRoot.setAttribute('aria-hidden', String(!shouldShow));
    document.body.classList.toggle('product-sticky-cart-active', shouldShow);

    this.updateHeight();
  }

  resetStickyLayout() {
    this.isVisible = false;
    document.body.classList.remove('product-sticky-cart-active');
    document.documentElement.style.setProperty(
      '--product-sticky-cart-height',
      '0px',
    );
  }

  updateHeight() {
    if (!this.stickyRoot) {
      this.resetStickyLayout();
      return;
    }

    const stickyHeight = this.isVisible ? this.stickyRoot.offsetHeight : 0;
    document.documentElement.style.setProperty(
      '--product-sticky-cart-height',
      `${stickyHeight}px`,
    );
  }

  destroy() {
    this.addToCartObserver?.disconnect();
    this.footerObserver?.disconnect();
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('variant:changed', this.onVariantChanged);
    this.teardownStickyRoot(true);
    this.resetStickyLayout();
    this.observedAddToCartRoot = null;
    this.observedFooter = null;
  }
}

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.onSubmitHandler = this.onSubmitHandler.bind(this);
    this.onSoftlensRootChange = this.onSoftlensRootChange.bind(this);
    this.onSoftlensRootInput = this.onSoftlensRootInput.bind(this);
    this.softlensRoot = null;
    this.preferredLoadingButton = null;
  }

  connectedCallback() {
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[type="submit"], [name="add"]');

    if (this.form) {
      this.form.removeEventListener('submit', this.onSubmitHandler);
      this.form.addEventListener('submit', this.onSubmitHandler);
    }

    this.initStickyCart();
    this.initSoftlensVariants();
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener('submit', this.onSubmitHandler);
    }

    const productSection = this.getProductSection();
    const stickyController = productSection?.__stickyProductBarController;

    if (stickyController && stickyController.productForm === this) {
      stickyController.destroy();
      delete productSection.__stickyProductBarController;
    }

    if (this.softlensRoot) {
      this.softlensRoot.removeEventListener(
        'change',
        this.onSoftlensRootChange,
      );
      this.softlensRoot.removeEventListener('input', this.onSoftlensRootInput);
      this.softlensRoot = null;
    }
  }

  getProductSection() {
    return this.closest('.product-section');
  }

  getAddToCartRoot() {
    return this.closest('[data-product-add-to-cart-block]');
  }

  initStickyCart() {
    const productSection = this.getProductSection();
    const addToCartRoot = this.getAddToCartRoot();

    if (
      !productSection ||
      !addToCartRoot ||
      this.closest('product-quick-view') ||
      this.closest('#DialogModal')
    ) {
      return;
    }

    if (addToCartRoot.dataset.productStickyEnabled !== 'true') {
      return;
    }

    if (productSection.__stickyProductBarController) {
      productSection.__stickyProductBarController.setProductForm(this);
      return;
    }

    const stickyRoot = addToCartRoot.querySelector(
      '[data-product-sticky-cart]',
    );

    if (!stickyRoot) {
      return;
    }

    productSection.__stickyProductBarController =
      new StickyProductBarController(productSection, addToCartRoot, this);
  }

  getContextRoot() {
    return (
      this.closest('product-quick-view') || this.getProductSection() || this
    );
  }

  getStickyButton() {
    const addToCartRoot = this.getAddToCartRoot();

    if (
      !addToCartRoot ||
      addToCartRoot.dataset.productStickyEnabled !== 'true'
    ) {
      return null;
    }

    const stickyController =
      this.getProductSection()?.__stickyProductBarController;
    const ownerId = addToCartRoot.dataset.productAddToCartBlock;

    if (stickyController?.stickyButton) {
      return stickyController.stickyButton;
    }

    if (ownerId) {
      const teleportedStickyButton = document.body.querySelector(
        `[data-product-sticky-cart][data-product-sticky-owner="${ownerId}"] .product-sticky-cart__button`,
      );

      if (teleportedStickyButton) {
        return teleportedStickyButton;
      }
    }

    return addToCartRoot.querySelector('.product-sticky-cart__button');
  }

  getSoftlensBuyNowButton() {
    return this.querySelector('[name="softlens_buy_now"]');
  }

  getSoftlensRoot() {
    const contextRoot = this.getContextRoot();

    if (contextRoot) {
      const scopedMatch = contextRoot.querySelector('[data-softlens-variants]');
      if (scopedMatch) {
        return scopedMatch;
      }
    }

    // Fallback for unexpected structures where variants are nested inside the form.
    return this.querySelector('[data-softlens-variants]');
  }

  initSoftlensVariants() {
    const nextRoot = this.getSoftlensRoot();

    if (this.softlensRoot && this.softlensRoot !== nextRoot) {
      this.softlensRoot.removeEventListener(
        'change',
        this.onSoftlensRootChange,
      );
      this.softlensRoot.removeEventListener('input', this.onSoftlensRootInput);
      this.softlensRoot = null;
    }

    if (!nextRoot) {
      return;
    }

    if (!this.softlensRoot) {
      nextRoot.addEventListener('change', this.onSoftlensRootChange);
      nextRoot.addEventListener('input', this.onSoftlensRootInput);
      this.softlensRoot = nextRoot;
    }

    this.syncSoftlensEyes(nextRoot);
    this.updateSoftlensBundleState(nextRoot);
    this.updateSoftlensSameVariantState(nextRoot);
  }

  onSoftlensRootInput(event) {
    const target = event.target;

    if (
      target instanceof HTMLInputElement &&
      /^softlens_(right|left)_(axis|cylinder)$/.test(target.name)
    ) {
      this.onSoftlensRootChange(event);
    }
  }

  onSoftlensRootChange(event) {
    if (!this.softlensRoot) {
      return;
    }

    const target = event.target;

    if (
      target instanceof HTMLInputElement &&
      (target.name === 'softlens_right_enabled' ||
        target.name === 'softlens_left_enabled')
    ) {
      this.syncSoftlensEyes(this.softlensRoot);
    }

    this.updateSoftlensBundleState(this.softlensRoot);
    this.updateSoftlensSameVariantState(this.softlensRoot);
  }

  syncSoftlensEyes(root) {
    this.syncSoftlensEye(root, 'right');
    this.syncSoftlensEye(root, 'left');
  }

  syncSoftlensEye(root, eye) {
    const toggle = root.querySelector(`input[name="softlens_${eye}_enabled"]`);
    const eyeControls = Array.from(
      root.querySelectorAll(`[data-softlens-eye-control="${eye}"]`),
    );

    if (!(toggle instanceof HTMLInputElement) || !eyeControls.length) {
      return;
    }

    const isChecked = toggle.checked;
    eyeControls.forEach((control) => {
      control.classList.toggle('opacity-60', !isChecked);
    });

    eyeControls.forEach((control) => {
      control.querySelectorAll('select').forEach((select) => {
        select.disabled = !isChecked;
      });
    });

    eyeControls.forEach((control) => {
      control.querySelectorAll('.input-select__trigger').forEach((trigger) => {
        if (!(trigger instanceof HTMLButtonElement)) {
          return;
        }

        trigger.disabled = !isChecked;
        trigger.setAttribute('aria-disabled', String(!isChecked));
      });
    });

    eyeControls.forEach((control) => {
      control.querySelectorAll('.input-qty__btn').forEach((button) => {
        if (button instanceof HTMLButtonElement) {
          button.disabled = !isChecked;
        }
      });
    });

    const quantityInput = root.querySelector(
      `input[name="softlens_${eye}_qty"]`,
    );

    if (!(quantityInput instanceof HTMLInputElement)) {
      return;
    }

    quantityInput.disabled = !isChecked;

    if (!isChecked) {
      if (Number(quantityInput.value || 0) !== 0) {
        quantityInput.value = '0';
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    if (Number(quantityInput.value || 0) < 1) {
      quantityInput.value = '1';
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  getSoftlensVariants(root) {
    const variantJsonElement = root.querySelector(
      '[data-softlens-variants-json]',
    );

    if (!variantJsonElement) {
      return [];
    }

    try {
      return JSON.parse(variantJsonElement.textContent || '[]');
    } catch (error) {
      console.error('Invalid softlens variant JSON', error);
      return [];
    }
  }

  getSoftlensBundleBoxQty(root) {
    const value = Number.parseInt(root?.dataset.softlensBundleBoxQty || '1', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  getSoftlensOptionName(select) {
    const row = select.closest('[data-softlens-option-row]');
    const label = row?.querySelector('[data-softlens-option-label]');
    return label?.textContent?.trim() || '';
  }

  getSoftlensOptionSelects(root, eye) {
    return Array.from(
      root.querySelectorAll(`select[name^="softlens_${eye}_option_"]`),
    ).sort((first, second) => {
      const firstPosition = Number.parseInt(
        first.name.split('_option_')[1] || '0',
        10,
      );
      const secondPosition = Number.parseInt(
        second.name.split('_option_')[1] || '0',
        10,
      );

      return firstPosition - secondPosition;
    });
  }

  resolveSoftlensSharedVariant(root) {
    if (this.getSoftlensBundleBoxQty(root) > 1) {
      return null;
    }

    const rightToggle = root.querySelector(
      'input[name="softlens_right_enabled"]',
    );
    const leftToggle = root.querySelector(
      'input[name="softlens_left_enabled"]',
    );

    if (
      !(rightToggle instanceof HTMLInputElement) ||
      !rightToggle.checked ||
      !(leftToggle instanceof HTMLInputElement) ||
      !leftToggle.checked
    ) {
      return null;
    }

    const variants = this.getSoftlensVariants(root);
    const rightSelects = this.getSoftlensOptionSelects(root, 'right');
    const leftSelects = this.getSoftlensOptionSelects(root, 'left');

    const rightVariant = this.resolveSoftlensVariant(
      variants,
      rightSelects.map((select) => select.value),
    );
    const leftVariant = this.resolveSoftlensVariant(
      variants,
      leftSelects.map((select) => select.value),
    );

    if (!rightVariant || !leftVariant || rightVariant.id !== leftVariant.id) {
      return null;
    }

    for (const field of ['axis', 'cylinder']) {
      const rightInput = root.querySelector(
        `input[name="softlens_right_${field}"]`,
      );
      const leftInput = root.querySelector(
        `input[name="softlens_left_${field}"]`,
      );

      if (rightInput?.value !== leftInput?.value) {
        return null;
      }
    }

    let powerValue = null;

    rightSelects.forEach((select) => {
      if (this.getSoftlensOptionName(select).trim().toLowerCase().includes('power')) {
        powerValue = select.value;
      }
    });

    return { variant: rightVariant, powerValue };
  }

  updateSoftlensSameVariantState(root) {
    const qtyRow = root.querySelector('[data-softlens-qty-row]');

    if (!qtyRow) {
      return;
    }

    const leftQtyControl = qtyRow.querySelector('[data-softlens-eye-control="left"]');
    const rightQtyControl = qtyRow.querySelector('[data-softlens-eye-control="right"]');

    if (!leftQtyControl || !rightQtyControl) {
      return;
    }

    const isShared = Boolean(this.resolveSoftlensSharedVariant(root));

    leftQtyControl.classList.toggle('hidden', isShared);
    rightQtyControl.classList.toggle('col-span-2', isShared);
    rightQtyControl.classList.toggle('flex', isShared);
    rightQtyControl.classList.toggle('justify-center', isShared);

    const leftQtyInput = leftQtyControl.querySelector(
      'input[name="softlens_left_qty"]',
    );
    const rightQtyInput = rightQtyControl.querySelector(
      'input[name="softlens_right_qty"]',
    );

    if (!(leftQtyInput instanceof HTMLInputElement)) {
      return;
    }

    if (isShared) {
      leftQtyInput.disabled = true;

      if (rightQtyInput instanceof HTMLInputElement) {
        leftQtyInput.value = rightQtyInput.value;
      }

      return;
    }

    // Not shared: defer to the eye toggle's own checked state rather than
    // unconditionally re-enabling, since syncSoftlensEye may not have run
    // in this same change cycle (e.g. an option select change, not a toggle).
    const leftToggle = root.querySelector(
      'input[name="softlens_left_enabled"]',
    );
    const isLeftEyeChecked =
      leftToggle instanceof HTMLInputElement && leftToggle.checked;

    leftQtyInput.disabled = !isLeftEyeChecked;
  }

  updateSoftlensBundleState(root) {
    const bundleBoxQty = this.getSoftlensBundleBoxQty(root);
    const progressElement = root.querySelector('[data-softlens-bundle-progress]');

    if (bundleBoxQty <= 1) {
      if (progressElement) {
        progressElement.textContent = '';
        progressElement.classList.remove('text-error');
        progressElement.classList.add('text-text-muted');
      }
      this.setSoftlensBundleValid(true);
      return;
    }

    const eyes = ['right', 'left'];
    let total = 0;

    eyes.forEach((eye) => {
      const toggle = root.querySelector(`input[name="softlens_${eye}_enabled"]`);
      if (!(toggle instanceof HTMLInputElement) || !toggle.checked) {
        return;
      }

      const quantityInput = root.querySelector(`input[name="softlens_${eye}_qty"]`);
      const quantity = Number.parseInt(quantityInput?.value || '0', 10);

      if (Number.isFinite(quantity)) {
        total += quantity;
      }
    });

    const bundleQuantity = total / bundleBoxQty;
    const isValid = total > 0 && Number.isInteger(bundleQuantity);
    const displayedBundleQuantity = Math.max(1, Math.ceil(bundleQuantity));
    const boxesRemaining =
      total <= 0 ? bundleBoxQty : bundleBoxQty - (total % bundleBoxQty);

    if (progressElement) {
      const template = isValid
        ? root.dataset.softlensBundleProgressLabel ||
          'Boxes selected: %boxes% (%bundles% %bundle_label%)'
        : root.dataset.softlensBundleRemainingLabel ||
          'Add %boxes% more to buy %bundles% %bundle_label%';
      const bundleLabel =
        displayedBundleQuantity === 1
          ? root.dataset.softlensBundleSingularLabel || 'bundle'
          : root.dataset.softlensBundlePluralLabel || 'bundles';

      progressElement.textContent = template
        .replace(/%boxes%/g, String(isValid ? total : boxesRemaining))
        .replace(/%bundles%/g, String(displayedBundleQuantity))
        .replace(/%bundle_label%/g, bundleLabel);

      progressElement.classList.toggle('text-error', !isValid);
      progressElement.classList.toggle('text-text-muted', isValid);
    }

    this.setSoftlensBundleValid(isValid);
  }

  setSoftlensBundleValid(isValid) {
    const buttons = [
      this.submitButton,
      this.getSoftlensBuyNowButton(),
      this.getStickyButton(),
    ].filter(Boolean);

    buttons.forEach((button) => {
      if (isValid) {
        button.removeAttribute('aria-disabled');
      } else {
        button.setAttribute('aria-disabled', 'true');
      }
    });
  }

  setSoftlensError(root, message) {
    const errorElement = root.querySelector('[data-softlens-error]');

    if (!errorElement) {
      return;
    }

    errorElement.textContent = message || '';
    errorElement.classList.toggle('hidden', !message);
  }

  resolveSoftlensVariant(variants, selectedValues) {
    if (!Array.isArray(variants) || !variants.length) {
      return null;
    }

    if (!selectedValues.length) {
      return variants.find((variant) => variant.available) || variants[0];
    }

    return variants.find((variant) => {
      if (!Array.isArray(variant.options)) {
        return false;
      }

      return selectedValues.every(
        (value, index) => variant.options[index] === value,
      );
    });
  }

  readSoftlensAxisCylinder(root, eye, { prefixWithEye = false } = {}) {
    const axisInput = root.querySelector(
      `input[name="softlens_${eye}_axis"]`,
    );
    const cylinderInput = root.querySelector(
      `input[name="softlens_${eye}_cylinder"]`,
    );

    if (axisInput && !axisInput.value) {
      throw new Error(
        root.dataset.softlensVariantError ||
          'Select a valid option combination before adding to cart.',
      );
    }

    if (cylinderInput && !cylinderInput.value) {
      throw new Error(
        root.dataset.softlensVariantError ||
          'Select a valid option combination before adding to cart.',
      );
    }

    const axisLabel = root.dataset.softlensAxisLabel || 'Axis';
    const cylinderLabel = root.dataset.softlensCylinderLabel || 'Cylinder';
    const eyeLabel = eye === 'right'
      ? root.dataset.softlensRightEyeLabel || 'Right'
      : root.dataset.softlensLeftEyeLabel || 'Left';
    const prefix = prefixWithEye ? `${eyeLabel} ` : '';
    const properties = {};

    if (axisInput) {
      properties[`${prefix}${axisLabel}`] = axisInput.value;
    }

    if (cylinderInput) {
      properties[`${prefix}${cylinderLabel}`] = cylinderInput.value;
    }

    return properties;
  }

  collectSoftlensItems(root) {
    const sharedVariant = this.resolveSoftlensSharedVariant(root);

    if (sharedVariant) {
      const quantityInput = root.querySelector(
        'input[name="softlens_right_qty"]',
      );
      const quantity = Number.parseInt(quantityInput?.value || '1', 10);

      if (!Number.isFinite(quantity) || quantity <= 0) {
        throw new Error(
          root.dataset.softlensQuantityError ||
            'Set at least one enabled eye quantity above zero.',
        );
      }

      const properties = {
        Eye: root.dataset.softlensBothEyesLabel || 'Left and Right',
        _left_eye_qty: String(quantity),
      };

      if (sharedVariant.powerValue !== null) {
        properties._left_eye_power = sharedVariant.powerValue;
      }

      properties._right_eye_qty = String(quantity);

      if (sharedVariant.powerValue !== null) {
        properties._right_eye_power = sharedVariant.powerValue;
      }

      Object.assign(
        properties,
        this.readSoftlensAxisCylinder(root, 'right', { prefixWithEye: true }),
      );
      Object.assign(
        properties,
        this.readSoftlensAxisCylinder(root, 'left', { prefixWithEye: true }),
      );

      return [
        {
          id: sharedVariant.variant.id,
          quantity,
          properties,
        },
      ];
    }

    const variants = this.getSoftlensVariants(root);
    const eyes = ['right', 'left'];
    const checkedEyes = eyes.filter((eye) => {
      const toggle = root.querySelector(
        `input[name="softlens_${eye}_enabled"]`,
      );
      return toggle instanceof HTMLInputElement && toggle.checked;
    });

    if (!checkedEyes.length) {
      throw new Error(
        root.dataset.softlensNoEyeError || 'Select at least one eye.',
      );
    }

    const includeEyeProperty = checkedEyes.length > 1;
    const items = [];

    checkedEyes.forEach((eye) => {
      const hasEyeControls = root.querySelector(
        `[data-softlens-eye-control="${eye}"]`,
      );

      if (!hasEyeControls) {
        return;
      }

      const quantityInput = root.querySelector(
        `input[name="softlens_${eye}_qty"]`,
      );
      const quantity = Number.parseInt(quantityInput?.value || '1', 10);

      if (!Number.isFinite(quantity) || quantity <= 0) {
        return;
      }

      const optionSelects = this.getSoftlensOptionSelects(root, eye);

      const selectedValues = optionSelects.map((select) => select.value);
      const matchedVariant = this.resolveSoftlensVariant(
        variants,
        selectedValues,
      );

      if (!matchedVariant) {
        throw new Error(
          root.dataset.softlensVariantError ||
            'Select a valid option combination before adding to cart.',
        );
      }

      let powerValue = null;

      optionSelects.forEach((select) => {
        const optionName = this.getSoftlensOptionName(select);

        if (optionName.trim().toLowerCase().includes('power')) {
          powerValue = select.value;
        }
      });

      const properties = {
        [`_${eye}_eye_qty`]: String(quantity),
      };

      if (powerValue !== null) {
        properties[`_${eye}_eye_power`] = powerValue;
      }

      Object.assign(properties, this.readSoftlensAxisCylinder(root, eye));

      if (includeEyeProperty) {
        properties.Eye = eye === 'right' ? 'Right' : 'Left';
      }

      const nextItem = {
        id: matchedVariant.id,
        quantity,
        properties,
      };

      items.push(nextItem);
    });

    if (!items.length) {
      throw new Error(
        root.dataset.softlensQuantityError ||
          'Set at least one enabled eye quantity above zero.',
      );
    }

    return items;
  }

  collectSoftlensBundleItem(root, bundleBoxQty) {
    const variants = this.getSoftlensVariants(root);
    const eyes = ['right', 'left'];
    const checkedEyes = eyes.filter((eye) => {
      const toggle = root.querySelector(
        `input[name="softlens_${eye}_enabled"]`,
      );
      return toggle instanceof HTMLInputElement && toggle.checked;
    });

    if (!checkedEyes.length) {
      throw new Error(
        root.dataset.softlensNoEyeError || 'Select at least one eye.',
      );
    }

    const powerLabel = root.dataset.softlensPowerLabel || 'Power';
    const colorLabel = root.dataset.softlensColorLabel || 'Color';
    const axisLabel = root.dataset.softlensAxisLabel || 'Axis';
    const cylinderLabel = root.dataset.softlensCylinderLabel || 'Cylinder';

    const properties = {};
    let totalQuantity = 0;
    let hasEntry = false;

    checkedEyes.forEach((eye) => {
      const quantityInput = root.querySelector(
        `input[name="softlens_${eye}_qty"]`,
      );
      const quantity = Number.parseInt(quantityInput?.value || '0', 10);

      if (!Number.isFinite(quantity) || quantity <= 0) {
        return;
      }

      const powerSelect = root.querySelector(
        `select[name="softlens_${eye}_power"]`,
      );
      const powerValue = powerSelect?.value || '';

      if (!powerValue) {
        throw new Error(
          root.dataset.softlensVariantError ||
            'Select a valid option combination before adding to cart.',
        );
      }

      const summaryParts = [`${powerLabel} ${powerValue}`];

      const colorSelect = root.querySelector(
        `select[name="softlens_${eye}_color"]`,
      );

      if (colorSelect) {
        const colorValue = colorSelect.value;

        if (!colorValue) {
          throw new Error(
            root.dataset.softlensVariantError ||
              'Select a valid option combination before adding to cart.',
          );
        }

        summaryParts.push(`${colorLabel} ${colorValue}`);
      }

      const axisInput = root.querySelector(
        `input[name="softlens_${eye}_axis"]`,
      );

      if (axisInput) {
        const axisValue = axisInput.value;

        if (!axisValue) {
          throw new Error(
            root.dataset.softlensVariantError ||
              'Select a valid option combination before adding to cart.',
          );
        }

        summaryParts.push(`${axisLabel} ${axisValue}`);
      }

      const cylinderInput = root.querySelector(
        `input[name="softlens_${eye}_cylinder"]`,
      );

      if (cylinderInput) {
        const cylinderValue = cylinderInput.value;

        if (!cylinderValue) {
          throw new Error(
            root.dataset.softlensVariantError ||
              'Select a valid option combination before adding to cart.',
          );
        }

        summaryParts.push(`${cylinderLabel} ${cylinderValue}`);
      }

      const boxLabel =
        quantity === 1
          ? root.dataset.softlensBoxSingularLabel || 'Box'
          : root.dataset.softlensBoxPluralLabel || 'Boxes';
      summaryParts.push(`${quantity} ${boxLabel}`);

      const eyePropertyLabel =
        eye === 'right'
          ? root.dataset.softlensRightEyePropertyLabel
          : root.dataset.softlensLeftEyePropertyLabel;

      properties[eyePropertyLabel || (eye === 'right' ? 'Right Eye' : 'Left Eye')] =
        summaryParts.join(' | ');

      properties[`_${eye}_eye_power`] = powerValue;
      properties[`_${eye}_eye_qty`] = String(quantity);

      totalQuantity += quantity;
      hasEntry = true;
    });

    if (!hasEntry) {
      throw new Error(
        root.dataset.softlensQuantityError ||
          'Set at least one enabled eye quantity above zero.',
      );
    }

    if (totalQuantity <= 0 || totalQuantity % bundleBoxQty !== 0) {
      throw new Error(
        root.dataset.softlensBundleQtyError ||
          `Total boxes must be a multiple of ${bundleBoxQty}.`,
      );
    }

    const variant = this.resolveSoftlensVariant(variants, []);

    if (!variant) {
      throw new Error(
        root.dataset.softlensVariantError ||
          'Select a valid option combination before adding to cart.',
      );
    }

    return {
      id: variant.id,
      quantity: totalQuantity / bundleBoxQty,
      properties,
    };
  }

  async addToCartRequest(body) {
    const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body,
    });

    const data = await response.json();

    if (!response.ok) {
      const failureMessage =
        data?.message || data?.description || 'Failed to add product to cart.';
      const error = new Error(failureMessage);
      error.cartResponse = data;
      throw error;
    }

    return data;
  }

  escapeHtml(value) {
    const text = String(value || '');
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  showAddToCartErrorModal(message) {
    const dialogModal = document.getElementById('DialogModal');

    if (
      !dialogModal ||
      typeof dialogModal.setContent !== 'function' ||
      typeof dialogModal.openModal !== 'function'
    ) {
      return false;
    }

    if (typeof dialogModal.setSize === 'function') {
      dialogModal.setSize('small');
    }

    const safeMessage = this.escapeHtml(
      message || 'Failed to add product to cart.',
    );

    dialogModal.setContent(`
      <div class="container text-center py-10 md:py-20">
        <p class="body-base text-text-dark">${safeMessage}</p>
      </div>
    `);
    dialogModal.openModal();

    return true;
  }

  async addSoftlensItemToCart(item) {
    const formData = new FormData();
    formData.append('id', String(item.id));
    formData.append('quantity', String(item.quantity));

    if (item.properties) {
      Object.entries(item.properties).forEach(([key, value]) => {
        formData.append(`properties[${key}]`, value);
      });
    }

    return this.addToCartRequest(formData);
  }

  setLoadingState(isLoading, activeButton = null) {
    const buttons = [
      this.submitButton,
      this.getStickyButton(),
      this.getSoftlensBuyNowButton(),
    ].filter(Boolean);

    buttons.forEach((button) => {
      if (isLoading) {
        button.setAttribute('aria-disabled', true);

        // Only add loading animation to the clicked button
        if (activeButton === button) {
          button.classList.add('loading');
        } else {
          button.classList.remove('loading');
        }
        return;
      }

      button.removeAttribute('aria-disabled');
      button.classList.remove('loading');
    });
  }

  submitProductForm(preferredButton = null) {
    if (!this.form) {
      return;
    }

    if (preferredButton instanceof HTMLElement) {
      this.preferredLoadingButton = preferredButton;
    }

    if (typeof this.form.requestSubmit === 'function') {
      this.form.requestSubmit();
      return;
    }

    this.form.dispatchEvent(
      new Event('submit', {
        bubbles: true,
        cancelable: true,
      }),
    );
  }

  async animateToCart() {
    const transitionApi = window.CartAddTransition;

    if (!transitionApi || typeof transitionApi.start !== 'function') {
      return false;
    }

    return transitionApi.start({
      root: this.getContextRoot(),
    });
  }

  async onSubmitHandler(evt) {
    evt.preventDefault();
    const clickedButton =
      this.preferredLoadingButton || evt.submitter || this.submitButton;
    this.setLoadingState(true, clickedButton);

    const softlensRoot = this.getSoftlensRoot();
    const isSoftlensBuyNow =
      evt.submitter instanceof HTMLElement &&
      evt.submitter.getAttribute('name') === 'softlens_buy_now';

    try {
      if (softlensRoot) {
        this.setSoftlensError(softlensRoot, '');
        const bundleBoxQty = this.getSoftlensBundleBoxQty(softlensRoot);

        if (bundleBoxQty > 1) {
          const item = this.collectSoftlensBundleItem(softlensRoot, bundleBoxQty);
          await this.addSoftlensItemToCart(item);
        } else {
          const items = this.collectSoftlensItems(softlensRoot);

          for (const item of items) {
            await this.addSoftlensItemToCart(item);
          }
        }

        if (isSoftlensBuyNow) {
          window.location.href = `${window.Shopify.routes.root}checkout`;
          return;
        }
      } else {
        const formData = new FormData(this.form);
        await this.addToCartRequest(formData);
      }

      const cartRequest = fetch(window.Shopify.routes.root + 'cart.js').then(
        (cartResp) => cartResp.json(),
      );

      document.dispatchEvent(
        new CustomEvent('cart:pre-animate-to-cart', {
          bubbles: true,
        }),
      );

      const animationRequest = this.animateToCart();
      const [cart] = await Promise.all([cartRequest, animationRequest]);

      const cartPageSection = document.querySelector(
        '[data-cart-page-section]',
      );
      const cartPageForm = cartPageSection?.querySelector('cart-form');

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: {
            count: cart.item_count,
            source: cartPageForm ? 'cart-page' : undefined,
          },
        }),
      );

      if (
        cartPageForm &&
        typeof cartPageForm.refreshCartPageSection === 'function'
      ) {
        await cartPageForm.refreshCartPageSection();
      }
    } catch (error) {
      const errorMessage = error?.message || '';

      if (softlensRoot) {
        this.setSoftlensError(softlensRoot, errorMessage);
      }

      const dialogModal = document.getElementById('DialogModal');
      if (dialogModal.open) {
        alert(errorMessage);
      } else {
        this.showAddToCartErrorModal(errorMessage);
      }
      console.error(error);
    } finally {
      this.setLoadingState(false);
      this.preferredLoadingButton = null;

      if (softlensRoot) {
        this.updateSoftlensBundleState(softlensRoot);
      }
    }
  }
}

customElements.define('product-form', ProductForm);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange.bind(this));
    const variantsJson = this.querySelector('.product-variants-json');
    if (variantsJson) {
      this.variants = JSON.parse(variantsJson.textContent);
    }
  }

  onVariantChange() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    if (!fieldsets.length) return;

    const options = fieldsets.map((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      return checked ? checked.value : null;
    });

    const selectedVariant = this.variants.find((variant) => {
      return !options.some(
        (option, index) => variant.options[index] !== option,
      );
    });

    if (!selectedVariant) return;

    // Update URL only if we are taking action on the actual product page (not quick view modal)
    if (this.dataset.url && !this.closest('#DialogModal')) {
      window.history.replaceState(
        {},
        '',
        `${this.dataset.url}?variant=${selectedVariant.id}`,
      );
    }

    // Fetch Section Rendering API
    fetch(
      `${this.dataset.url}?variant=${selectedVariant.id}&section_id=${this.dataset.section}`,
    )
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        // Determine destination based on context (modal vs full page)
        const isModal = this.closest('#DialogModal');
        const destination = isModal
          ? this.closest('#DialogContent').firstElementChild
          : document.querySelector('.product-section');

        // Get the top-level element returned by the Section Rendering API
        const sourceSectionId = `shopify-section-${this.dataset.section}`;
        const sourceWrapper = html.getElementById(sourceSectionId);
        const source = sourceWrapper ? sourceWrapper.firstElementChild : null;

        if (source && destination && typeof window.wakaMorph === 'function') {
          window.wakaMorph(
            destination,
            source,
            {
              callbacks: {
                beforeNodeMorphed: (oldNode, newNode) => {
                  if (!(oldNode instanceof HTMLElement)) return;

                  // Stop morphing the shopify payment buttons so they don't break
                  if (oldNode.classList.contains('shopify-payment-button')) {
                    return false;
                  }

                  // Stop morphing the Swiper product-images component
                  // If we morph it, Swiper's internal DOM structure is destroyed
                  // and it resets to slide 0 on every size change
                  if (oldNode.tagName === 'PRODUCT-IMAGES') {
                    // We still want to update its wrapper attributes though (like data-initial-slide)
                    if (newNode instanceof HTMLElement) {
                      oldNode.setAttribute(
                        'data-initial-slide',
                        newNode.getAttribute('data-initial-slide') || '0',
                      );
                      oldNode.setAttribute(
                        'data-variant-id',
                        newNode.getAttribute('data-variant-id') || '',
                      );
                    }
                    return false;
                  }
                },
              },
            },
            {
              context: 'product-variant-change',
              root: destination,
            },
          );

          // Dispatch a global event so other components (like Swiper JS) know
          // the DOM has finished morphing and the variant has fully changed.
          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: selectedVariant,
              },
            }),
          );
        } else if (source && destination) {
          destination.innerHTML = source.innerHTML;
        }
      })
      .catch((e) => {
        console.error('Error fetching section variant data:', e);
      });
  }
}

customElements.define('variant-selects', VariantSelects);
